import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { FaEdit } from "react-icons/fa";

const AdminProfileAdd = () => {
  const Navigate = useNavigate();
  const token = localStorage.getItem("Token");

  const [user, setUser] = useState(null);
  const [updatedFields, setUpdatedFields] = useState({});
  const [isModified, setIsModified] = useState(false);

  // ✅ Fetch profile
  const getProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_URL}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.data) {
        const profile = res.data.data;

        // flatten address data
        const merged = {
          ...profile,
        };

        setUser(merged);
        localStorage.setItem("user", JSON.stringify(merged));
      }
    } catch (err) {
      if (err.response?.data?.message === "Unauthorized!") {
        Navigate("/");
      } else {
        toast.error("Failed to fetch profile");
      }
    }
  }, [token, Navigate]);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) setUser(JSON.parse(stored));
    getProfile();
  }, [getProfile]);

  // ✅ Handle field input
  const handleInputChange = (e, field) => {
    const { value } = e.target;
    setUpdatedFields((prev) => ({ ...prev, [field]: value }));
    setUser((prev) => ({ ...prev, [field]: value }));
    setIsModified(true);
  };

  // ✅ Handle save/update
  const handleSave = async () => {
    if (!isModified) {
      toast.info("No changes to save");
      return;
    }
    try {
      const formData = new FormData();

      // add updated fields
      Object.keys(updatedFields).forEach((key) => {
        const value = updatedFields[key];
        if (value !== null && value !== undefined) formData.append(key, value);
      });

      const res = await axios.put(
        `${process.env.REACT_APP_URL}/auth/profile`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (res.status === 200) {
        toast.success("Profile updated successfully");
        await getProfile();
        setIsModified(false);
        setUpdatedFields({});
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update");
    }
  };

  if (!user)
    return (
      <div className="al">
        <div className="al-card" style={{ textAlign: "center", padding: "48px 20px", color: "var(--color-text-secondary)" }}>Loading...</div>
      </div>
    );

  const initials = (user?.name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");

  return (
    <div className="al">
      <Toaster position="top-center" />

      <div className="al-head">
        <div>
          <h1 className="al-title">Update Profile</h1>
          <p className="al-sub">Manage your account information.</p>
        </div>
        <div className="al-head__actions">
          <button type="button" className="al-btn" onClick={() => Navigate(-1)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
        </div>
      </div>

      <div className="al-idbanner">
        <div className="al-idbanner__icon">{initials}</div>
        <div>
          <div className="al-idbanner__name">{user?.name || "—"}</div>
          <div className="al-idbanner__sub">{user?.email || "—"}</div>
        </div>
        <span className="al-pill" style={{ marginLeft: "auto", textTransform: "capitalize", background: "var(--color-info-bg)", color: "var(--color-info-text)" }}>
          {user?.user_type || "Admin"}
        </span>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <div className="al-card">
          <div className="al-cardhead">
            <div className="al-cardhead__icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg></div>
            <h3 className="al-cardhead__title">Personal Information</h3>
          </div>
          <div className="al-form">
            {[
              { label: "Name", field: "name" },
              { label: "Email", field: "email", readOnly: true },
              { label: "Mobile Number", field: "phone" },
            ].map(({ label, field, readOnly }) => (
              <div key={field}>
                <label className="al-label">{label}</label>
                <input
                  type="text"
                  className="al-input"
                  readOnly={readOnly}
                  style={readOnly ? { background: "var(--color-surface-2)", cursor: "not-allowed" } : undefined}
                  value={user[field] || ""}
                  onChange={(e) => handleInputChange(e, field)}
                />
              </div>
            ))}
          </div>
          <div className="al-actionbar">
            <button
              type="button"
              className="al-btn al-btn--primary"
              disabled={!isModified}
              style={!isModified ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
              onClick={handleSave}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              Update Profile
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminProfileAdd;
